// Pure helpers voor de regeltabel van de Offerte Generator: groeperen in secties
// (= de groepen in Gripp), herordenen, totalen. De regels blijven één platte lijst
// met `section` per regel — precies zoals create-gripp ze verwacht — maar we houden
// die lijst ALTIJD gegroepeerd: elke sectie één aaneengesloten blok, in de volgorde
// zoals getoond. Zo schrijft "Maak concept-offerte in Gripp" de volgorde en groepen
// precies zoals het scherm ze laat zien.

import { PRODUCT_VELDEN_LEEG } from './basisofferte';

let teller = 0;
export function nieuweSleutel() {
  teller += 1;
  return `r${Date.now().toString(36)}${teller.toString(36)}`;
}

// Elke regel een stabiele sleutel (voor slepen en React-keys); bestaande blijven staan.
export function metSleutels(lines) {
  let veranderd = false;
  const uit = (lines || []).map((l) => {
    if (l && l._k) return l;
    veranderd = true;
    return { ...(l || {}), _k: nieuweSleutel() };
  });
  return veranderd ? uit : lines;
}

const sectieVan = (l) => String(l?.section ?? '').trim();

// [{ naam, aantal, regels: [line...] }] op volgorde van eerste voorkomen.
// `aantal` = het "N x" op het groepskopje (sectionAmount, staat op een regel van de sectie).
export function groepeer(lines) {
  const groepen = [];
  const opNaam = new Map();
  for (const l of lines || []) {
    const naam = sectieVan(l);
    let g = opNaam.get(naam);
    if (!g) {
      g = { naam, aantal: null, regels: [] };
      opNaam.set(naam, g);
      groepen.push(g);
    }
    if (g.aantal == null && l && l.sectionAmount != null && l.sectionAmount !== '') g.aantal = l.sectionAmount;
    g.regels.push(l);
  }
  // Regels zonder sectie staan in Gripp vóór het eerste groepskopje (ze krijgen geen
  // eigen kop); dus ook in het scherm bovenaan, anders schrijft Gripp ze onder de vorige groep.
  const zonder = groepen.findIndex((g) => g.naam === '');
  if (zonder > 0) groepen.unshift(groepen.splice(zonder, 1)[0]);
  return groepen;
}

// Terug naar de platte lijst; sectionAmount komt op de EERSTE regel van elke sectie
// (daar leest create-gripp hem), en nergens anders.
export function vlak(groepen) {
  const uit = [];
  for (const g of groepen || []) {
    g.regels.forEach((l, i) => {
      const { sectionAmount, ...rest } = l;
      const regel = { ...rest, section: g.naam };
      if (i === 0 && g.aantal != null && g.aantal !== '') regel.sectionAmount = g.aantal;
      uit.push(regel);
    });
  }
  return uit;
}

// Lijst gegroepeerd maken (aaneengesloten secties). Laat een al gegroepeerde lijst ongemoeid.
export function normaliseer(lines) {
  return vlak(groepeer(lines));
}

function zoekRegel(groepen, sleutel) {
  for (let gi = 0; gi < groepen.length; gi += 1) {
    const ri = groepen[gi].regels.findIndex((l) => l._k === sleutel);
    if (ri >= 0) return { gi, ri };
  }
  return null;
}

const kopie = (groepen) => groepen.map((g) => ({ ...g, regels: [...g.regels] }));

// Regel `sleutel` naar sectie `naarSectie`, op positie `naarIndex` (binnen die sectie;
// undefined = achteraan). Geeft de nieuwe platte lijst.
export function verplaatsRegel(lines, sleutel, naarSectie, naarIndex) {
  const groepen = kopie(groepeer(lines));
  const plek = zoekRegel(groepen, sleutel);
  if (!plek) return lines;
  const [regel] = groepen[plek.gi].regels.splice(plek.ri, 1);
  let doel = groepen.find((g) => g.naam === String(naarSectie ?? '').trim());
  if (!doel) {
    doel = { naam: String(naarSectie ?? '').trim(), aantal: null, regels: [] };
    groepen.splice(plek.gi + 1, 0, doel);
  }
  const index = naarIndex == null ? doel.regels.length : Math.max(0, Math.min(naarIndex, doel.regels.length));
  doel.regels.splice(index, 0, regel);
  // Een sectie die leeg raakt verdwijnt (een groepskop zonder regels heeft in Gripp geen zin).
  return vlak(groepen.filter((g) => g.regels.length));
}

// Toetsenbord: één plek omhoog (-1) of omlaag (+1); over de sectiegrens heen naar
// de onderkant van de vorige of de bovenkant van de volgende sectie.
export function schuifRegel(lines, sleutel, richting) {
  const groepen = groepeer(lines);
  const plek = zoekRegel(groepen, sleutel);
  if (!plek) return lines;
  const g = groepen[plek.gi];
  if (richting < 0) {
    if (plek.ri > 0) return verplaatsRegel(lines, sleutel, g.naam, plek.ri - 1);
    if (plek.gi === 0) return lines;
    return verplaatsRegel(lines, sleutel, groepen[plek.gi - 1].naam);
  }
  if (plek.ri < g.regels.length - 1) return verplaatsRegel(lines, sleutel, g.naam, plek.ri + 1);
  if (plek.gi === groepen.length - 1) return lines;
  return verplaatsRegel(lines, sleutel, groepen[plek.gi + 1].naam, 0);
}

export function verplaatsSectie(lines, naam, naarIndex) {
  const groepen = kopie(groepeer(lines));
  const van = groepen.findIndex((g) => g.naam === naam);
  // Regels zonder sectie staan altijd bovenaan (geen groepskop in Gripp): niet verplaatsbaar,
  // en een benoemde sectie kan er niet boven.
  if (van < 0 || naam === '') return lines;
  const minimum = groepen[0]?.naam === '' ? 1 : 0;
  const naar = Math.max(minimum, Math.min(naarIndex, groepen.length - 1));
  if (naar === van) return lines;
  const [g] = groepen.splice(van, 1);
  groepen.splice(naar, 0, g);
  return vlak(groepen);
}

export function schuifSectie(lines, naam, richting) {
  const groepen = groepeer(lines);
  const van = groepen.findIndex((g) => g.naam === naam);
  if (van < 0) return lines;
  return verplaatsSectie(lines, naam, van + (richting < 0 ? -1 : 1));
}

// Hernoemen; bestaat de nieuwe naam al, dan schuiven de regels bij die sectie aan.
export function hernoemSectie(lines, oud, nieuw) {
  const doel = String(nieuw ?? '').trim();
  const bestaand = groepeer(lines).find((g) => g.naam === doel && doel !== oud);
  const uit = normaliseer((lines || []).map((l) => (sectieVan(l) === oud ? { ...l, section: doel } : l)));
  // Samenvoegen met een bestaande sectie: die behoudt haar eigen kop-aantal.
  return bestaand ? zetSectieAantal(uit, doel, bestaand.aantal) : uit;
}

export function zetSectieAantal(lines, naam, aantal) {
  const groepen = kopie(groepeer(lines));
  const g = groepen.find((x) => x.naam === naam);
  if (!g) return lines;
  g.aantal = aantal === '' || aantal == null ? null : aantal;
  return vlak(groepen);
}

export const legeRegel = (sectie) => ({
  _k: nieuweSleutel(), section: sectie, description: '', detail: '', amount: 1, unit: 'stuk',
  sellingprice: 0, needsReview: true,
});

export function voegRegelToe(lines, sectie) {
  const groepen = kopie(groepeer(lines));
  const g = groepen.find((x) => x.naam === sectie);
  if (g) g.regels.push(legeRegel(sectie));
  else groepen.push({ naam: sectie, aantal: null, regels: [legeRegel(sectie)] });
  return vlak(groepen);
}

export function nieuweSectieNaam(lines, basis = 'Nieuwe sectie') {
  const namen = new Set(groepeer(lines).map((g) => g.naam));
  if (!namen.has(basis)) return basis;
  let i = 2;
  while (namen.has(`${basis} ${i}`)) i += 1;
  return `${basis} ${i}`;
}

export function voegSectieToe(lines, naam) {
  return [...normaliseer(lines), legeRegel(naam || nieuweSectieNaam(lines))];
}

// Binnen de groepen verwijderen, zodat het kop-aantal (op de eerste regel) naar de
// nieuwe eerste regel van de sectie gaat in plaats van te verdwijnen.
export function verwijderRegel(lines, sleutel) {
  const groepen = kopie(groepeer(lines))
    .map((g) => ({ ...g, regels: g.regels.filter((l) => l._k !== sleutel) }))
    .filter((g) => g.regels.length);
  return vlak(groepen);
}

export function verwijderSectie(lines, naam) {
  return (lines || []).filter((l) => sectieVan(l) !== naam);
}

const getal = (v) => {
  const n = Number(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

export const regelTotaal = (l) => getal(l?.sellingprice) * getal(l?.amount);
export const regelInkoop = (l) => getal(l?.buyingprice) * getal(l?.amount);

// BTW per regel: 0% voor de Gripp-tarieven nul/verlegd/intern, anders 21%.
const NUL_BTW = new Set([25, 26, 231]);
// Zonder btw op de regel geldt het btw-tarief van de klant (zoals create-gripp doet).
export const btwFactor = (l, standaardBtw = null) => {
  const vat = l?.vat ?? standaardBtw;
  return NUL_BTW.has(Number(vat)) ? 0 : 0.21;
};

// Btw-tarief van de klant; onbekend (bv. offerte geladen) → het tarief dat alle regels
// met een eigen btw gemeen hebben (zo staat het ook in Gripp).
export function standaardBtwVan(lines, standaardBtw = null) {
  if (standaardBtw != null) return standaardBtw;
  const tarieven = new Set((lines || []).map((l) => l?.vat).filter((v) => v != null && v !== ''));
  return tarieven.size === 1 ? [...tarieven][0] : null;
}

export function totalen(lines, standaardBtwKlant = null) {
  const standaardBtw = standaardBtwVan(lines, standaardBtwKlant);
  const perSectie = {};
  let excl = 0;
  let btw = 0;
  let inkoop = 0;
  for (const l of lines || []) {
    const t = regelTotaal(l);
    const naam = sectieVan(l);
    perSectie[naam] = (perSectie[naam] || 0) + t;
    excl += t;
    btw += t * btwFactor(l, standaardBtw);
    inkoop += regelInkoop(l);
  }
  const rond = (n) => Math.round(n * 100) / 100;
  const marge = inkoop > 0 ? rond(excl - inkoop) : null;
  return {
    perSectie: Object.fromEntries(Object.entries(perSectie).map(([k, v]) => [k, rond(v)])),
    excl: rond(excl), btw: rond(btw), incl: rond(excl + btw), inkoop: rond(inkoop),
    marge, margePct: marge != null && excl > 0 ? Math.round((marge / excl) * 100) : null,
    btwPct: excl > 0 ? Math.round((btw / excl) * 100) : 21,
  };
}

// Productcode uit een Gripp-naam: "Monteren (20004)" → "20004".
export function productCode(naam) {
  const m = String(naam || '').match(/\((\d{4,6})\)\s*$/);
  return m ? m[1] : null;
}
const zonderCode = (naam) => String(naam || '').replace(/\s*\(\d{4,6}\)\s*$/, '').replace(/\s+/g, ' ').trim();

// Opties voor de product-combobox: eerst de producten uit de basisoffertes (met
// eenheid en standaardprijs), daarna de rest van de Gripp-catalogus.
export function productOpties(catalogus, producten) {
  const uit = [];
  const gezien = new Set();
  for (const c of catalogus || []) {
    if (!c || !c.product) continue;
    uit.push({
      soort: 'basis', label: c.product, code: c.code || null, productId: c.productId ?? null,
      eenheid: c.eenheid || null, unitId: c.eenheidId ?? null, prijs: c.prijs ?? null,
      inkoop: c.inkoop ?? 0, verborgen: !!c.verborgen, omschrijving: c.omschrijving || '', bron: c,
    });
    if (c.productId != null) gezien.add(Number(c.productId));
  }
  for (const p of producten || []) {
    if (!p || gezien.has(Number(p.id))) continue;
    uit.push({ soort: 'gripp', label: zonderCode(p.name), code: productCode(p.name), productId: p.id });
  }
  return uit;
}

const EENHEDEN = ['stuk', 'uur', 'dag', 'km', 'minuut film', 'prijs'];

// Wat een gekozen product in de regel invult. Basisproduct: eenheid, prijs, inkoop,
// verborgen en (als er nog niets staat) de standaardomschrijving.
export function regelUitOptie(regel, optie) {
  if (!optie) return regel;
  const basis = optie.soort === 'basis';
  const patch = {
    ...PRODUCT_VELDEN_LEEG,
    description: optie.label, product: optie.label, productId: optie.productId ?? null,
    code: optie.code || null,
  };
  if (basis) {
    if (optie.eenheid) patch.unit = EENHEDEN.includes(optie.eenheid) ? optie.eenheid : 'dag';
    if (optie.unitId != null) patch.unitId = optie.unitId;
    if (optie.prijs != null) patch.sellingprice = optie.prijs;
    patch.buyingprice = optie.inkoop || 0;
    patch.hidden = !!optie.verborgen;
    if (!regel?.detail && optie.omschrijving) patch.detail = optie.omschrijving;
    const b = optie.bron?.bron || {};
    patch.basis = {
      offerId: b.offerId ?? null, naam: b.naam || null, gesynct: b.gesynct || null,
      bijgewerkt: b.bijgewerkt || null, code: optie.code, product: optie.label,
      prijs: optie.prijs, eenheid: optie.eenheid,
      ...(optie.bron?.override ? { override: optie.bron.override } : {}),
    };
    patch.priceSource = 'basis';
    patch.needsReview = false;
  }
  return { ...regel, ...patch };
}

// Historie compact: "~€80 · n=535" en de afwijking alleen als die er is.
export function historieTekst(pc) {
  if (!pc || pc.level === 'none' || pc.median == null) return null;
  const dev = Number(pc.deviationPct) || 0;
  return {
    tekst: `~€${pc.median} · n=${pc.count}`,
    afwijking: Math.abs(dev) >= 5 ? `${dev > 0 ? '+' : ''}${dev}%` : null,
    ernst: pc.level === 'warn' ? 'warn' : (Math.abs(dev) >= 5 ? 'check' : 'ok'),
  };
}
